import { BookableAppointmentTypeDeletionValidation } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/bookable_appointment_type.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { Trans, useTranslation } from '@frontend/i18n';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { ConfirmationModal, ModalControlModalProps, useAlert } from '@frontend/design-system';
import { ServiceTypesSettingsTrackingIds } from '../../trackingIds/service-types-settings-tracking';

type DeleteServiceTypeModalProps = {
  modalProps: ModalControlModalProps;
  serviceType: SchedulerV3.Types.SelectedServiceType | null;
  onCancel: () => void;
  isIntegratedOffice: boolean;
  deleteValidation: BookableAppointmentTypeDeletionValidation | undefined;
};

export const DeleteServiceTypeModal = ({
  modalProps,
  serviceType,
  onCancel,
  isIntegratedOffice,
  deleteValidation,
}: DeleteServiceTypeModalProps) => {
  const { t } = useTranslation('schedule');
  const alerts = useAlert();
  const { navigate } = useSettingsNavigate();
  const scheduleDefinitionCount = deleteValidation?.blockingScheduleDefinitionIds?.length || 0;
  const appointmentsCount = deleteValidation?.affectedAppointmentCount || 0;
  const exceptionsCount = deleteValidation?.affectedExceptionsCount || 0;
  const hasScheduleDefinition = scheduleDefinitionCount > 0;
  const hasAppointments = appointmentsCount > 0;
  const hasExceptions = exceptionsCount > 0;
  const bookableAppointmentTypeName =
    deleteValidation?.bookableAppointmentTypeName || serviceType?.bookableAppointmentType?.displayName || '';
  const modalMaxWidth = hasScheduleDefinition || hasExceptions || hasAppointments ? 600 : 400;

  const { mutateAsync: deleteServiceType } = SchedulerV3.Mutations.useDeleteBookableAppointmentTypesMutation();

  const handleOnDelete = async () => {
    if (!serviceType?.appointmentTypeGroup) return;

    const locationId =
      serviceType.bookableAppointmentType?.locationId ||
      serviceType.appointmentTypeGroup.appointmentType.locationId ||
      '';

    if (locationId) {
      SchedulerV3.trackPendoEvent(ServiceTypesSettingsTrackingIds.events.serviceDeleteClicked, {
        isIntegrated: isIntegratedOffice,
        locationId,
      });
    }

    try {
      await deleteServiceType({
        bookableAppointmentTypeIds: serviceType?.bookableAppointmentType?.id
          ? [serviceType.bookableAppointmentType.id]
          : [],
        locationId: serviceType?.bookableAppointmentType?.locationId || '',
      });
    } catch {
      alerts.error(t('Error in deleting the service type. Please try again.'));
    }
  };

  const getModalHeading = () => {
    if (hasScheduleDefinition) {
      return t('This service type cannot be deleted because it is currently used in {{count}} availability rules', {
        count: scheduleDefinitionCount,
      });
    }

    if (hasExceptions) {
      return t('This service type cannot be deleted because it is currently used in {{count}} exceptions', {
        count: exceptionsCount,
      });
    }

    if (hasAppointments) {
      return t('This service type has existing appointments associated with it');
    }

    return t('Are you sure your want to delete {{bookableAppointmentTypeName}}?', { bookableAppointmentTypeName });
  };

  const getModalContentText = () => {
    if (hasScheduleDefinition) {
      return t('Remove this service type from all availability rules before deleting.');
    }

    if (hasExceptions) {
      return t('Remove this service type from all exceptions before deleting.');
    }

    if (hasAppointments) {
      return (
        <Trans t={t}>
          Deleting this service type will not cancel these appointments
          <br />
          <br />
          Are you sure you want to delete {bookableAppointmentTypeName}?
        </Trans>
      );
    }

    return t('This action cannot be undone.');
  };

  const getSecondaryButtonProps = () => {
    if (hasScheduleDefinition) {
      return {
        cancelLabel: t('Go to Availability Rules'),
        onCancel: () =>
          navigate({
            to: '/schedule/availability/:locationId',
            params: { locationId: serviceType?.bookableAppointmentType?.locationId || '' },
          }),
      };
    }

    if (hasExceptions) {
      return {
        cancelLabel: t('Go to Exceptions'),
        onCancel: () =>
          navigate({
            to: '/schedule/availability/:locationId',
            params: { locationId: serviceType?.bookableAppointmentType?.locationId || '' },
            search: { tab: 'rule-exceptions' },
          }),
      };
    }

    return {
      cancelLabel: t('Cancel'),
      onCancel,
    };
  };

  const getPrimaryButtonProps = () => {
    if (hasScheduleDefinition || hasExceptions) {
      return {
        confirmLabel: t('Okay'),
        onConfirm: onCancel,
      };
    }

    return {
      confirmLabel: t('Delete'),
      onConfirm: handleOnDelete,
      destructive: true,
    };
  };

  return (
    <ConfirmationModal
      {...modalProps}
      maxWidth={modalMaxWidth}
      title={getModalHeading()}
      message={getModalContentText()}
      onClose={modalProps.onClose}
      {...getPrimaryButtonProps()}
      {...getSecondaryButtonProps()}
    />
  );
};
